import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import LogRocket from "logrocket";
import { Loader } from "lucide-react";
import { menuItems } from "../context/roles";
import { resolveFirstAllowedPath } from "../context/resolvePermissionByPath";
import { useTenantConfig } from "../context/TenantConfigContext";
import "./../common.css";

const STORAGE_KEY = "auth:loginInProgress";

const AdminLogin = () => {
  const { login, isAuthenticated, userValidated, userPermissions, errorInfo } = useAuth();
  const { tenantConfig, error: tenantConfigError, canLogin } = useTenantConfig();
  const navigate = useNavigate();

  const [isLoggingIn, setIsLoggingIn] = useState(
    () => sessionStorage.getItem(STORAGE_KEY) === "1"
  );

  const setLogRocketUser = (user) => {
    if (
      process.env.NODE_ENV === "production" &&
      process.env.REACT_APP_LOG_ROCKET_KEY
    ) {
      LogRocket.identify(user.email, {
        userName: user.username,
        name: user.fullName,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        token: user.access_token,
        environment: process.env.NODE_ENV,
      });
    }
  };

  // Start SSO login
  const startLogin = () => {
    if (!canLogin) return;

    setIsLoggingIn(true);
    sessionStorage.setItem(STORAGE_KEY, "1");
    login();
  };

  // Redirect AFTER login & permissions loaded
  useEffect(() => {
    const isLoginFlow = sessionStorage.getItem(STORAGE_KEY) === "1";

    if (isLoginFlow && isAuthenticated && userValidated && userPermissions?.length > 0) {
      const firstAllowedPath = resolveFirstAllowedPath(menuItems, userPermissions);
      sessionStorage.removeItem(STORAGE_KEY);
      navigate(firstAllowedPath || "/unauthorized", { replace: true });
    }
  }, [isAuthenticated, userValidated, userPermissions, navigate]);

  // Stop loader on error
  useEffect(() => {
    if (errorInfo) {
      setIsLoggingIn(false);
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }, [errorInfo]);

  useEffect(() => {
    if (!canLogin) {
      setIsLoggingIn(false);
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }, [canLogin]);

  const showLoader = isLoggingIn;
  const logoUrl = tenantConfig.loginLogoUrl || "/images/champs/Champs-Logo-Clr.png";
  const loginBackgroundStyle = {
    backgroundColor: tenantConfig.loginBackgroundColor || undefined,
    backgroundImage: tenantConfig.loginBackgroundImageUrl
      ? `url(${tenantConfig.loginBackgroundImageUrl})`
      : undefined,
    backgroundSize: tenantConfig.loginBackgroundImageUrl ? "cover" : undefined,
    backgroundPosition: tenantConfig.loginBackgroundImageUrl ? "center" : undefined,
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-8 bg-page-background"
      style={loginBackgroundStyle}
    >
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden text-center">
        <div className="p-8">
          <div className="flex items-center justify-center gap-2 mb-5">
            <img src={logoUrl} alt="Tenant logo" className="h-8 w-auto" />
          </div>

          <h2 className="text-2xl font-semibold mb-2 text-heading">{tenantConfig.loginHeading}</h2>
          <p className="text-slate-500 mb-1">{tenantConfig.loginSubheading}</p>
          {process.env.REACT_APP_ENVIRONMENT_INDICATOR_TEXT ? (
            <p className="text-xs text-slate-400 mb-8">
              ({process.env.REACT_APP_ENVIRONMENT_INDICATOR_TEXT})
            </p>
          ) : (
            <div className="mb-8" />
          )}

          {showLoader ? (
            <div className="flex items-center justify-center py-3 text-slate-700">
              <Loader className="h-5 w-5 mr-2 animate-spin" />
              <span>Logging in...</span>
            </div>
          ) : (
            <button
              onClick={startLogin}
              disabled={!canLogin}
              className="btn-primary w-72 max-w-full inline-flex justify-center text-white px-6 py-3 rounded-md transition-all font-medium mx-auto disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {tenantConfig.loginButtonText}
            </button>
          )}

          {tenantConfigError && (
            <p className="mt-4 text-red-600 font-medium">{tenantConfigError}</p>
          )}
          {errorInfo && <p className="mt-4 text-red-600 font-medium">{errorInfo}</p>}
        </div>

        <div className="border-t border-slate-200 px-6 py-4">
          <p className="text-sm text-slate-500 inline-flex items-center gap-2">
            Secured by
            <img src={logoUrl} alt="Tenant logo" className="h-4 w-auto" />
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
